<script setup>
/* global H */

import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";

// Props
const props = defineProps({
  minHeight: {
    type   : [Number, String],
    default: 400
  }
});

// Constants
const apiKey = process.env.VUE_APP_HERE_MAP_API_KEY ?? null;
const geocodeUrl = "https://geocode.search.hereapi.com/v1/geocode"

// How tight the view should be, depending on how precise the geocoding result is.
const ZOOM_BY_RESULT_TYPE = {
  houseNumber       : 16,
  addressBlock      : 15,
  intersection      : 15,
  place             : 15,
  street            : 15,
  postalCodePoint   : 14,
  locality          : 12,
  administrativeArea: 9
};
const DEFAULT_ZOOM = 14;

const STATUS_MESSAGE_KEYS = {
  empty      : "components.projectMap.missingLocation",
  notFound   : "components.projectMap.locationNotFound",
  error      : "components.projectMap.geocodingError"
};

// Vuex
const store  = useStore();

// Reactive variable
const platform      = ref(null);
const defaultLayers = ref(null);
const map           = ref(null);
const mapUi         = ref(null);
const mapBehavior   = ref(null);
const mapMarker     = ref(null);
const mapBubble     = ref(null);
const mapCenter     = ref({lat: null, lng: null});
const mapZoom       = ref(DEFAULT_ZOOM);
const mapContainer  = ref(null);
// 'loading' | 'ready' | 'empty' | 'notFound' | 'error'
const status        = ref("loading");

// Non reactive variable
let resizeObserver = null;
let requestId      = 0;
let lastQuery      = null;

// Computed properties
const projectInfo = computed(() => {
  return store.state.projectInfo;
});

const statusMessageKey = computed(() => {
  return STATUS_MESSAGE_KEYS[status.value] ?? null;
});

const getAddressQuery = (info) => {
  const location = info?.location ?? {};
  return [
    location.number,
    location.street,
    location.city,
    location.province,
    location.postalCode,
    location.country,
  ]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .join(", ");
};

// Functions
const resizeMap = () => {
  if (map.value) {
    map.value.getViewPort().resize();
  }
};

// The map lives inside a tab that Vuetify hides with `display: none`, which collapses the
// container to zero. Observing the container lets us resize the viewport when it comes back.
const observeContainerSize = () => {
  if (typeof ResizeObserver === "undefined" || resizeObserver || !mapContainer.value) {
    return;
  }
  resizeObserver = new ResizeObserver(resizeMap);
  resizeObserver.observe(mapContainer.value);
};

const initMap = () => {
  if (map.value === null && mapContainer.value) {
    let localMap      = new H.Map(
        mapContainer.value,
        defaultLayers.value.vector.normal.map,
        {
          zoom  : mapZoom.value,
          center: {
            lat: mapCenter.value.lat,
            lng: mapCenter.value.lng
          }
        });
    mapBehavior.value = new H.mapevents.Behavior(new H.mapevents.MapEvents(localMap));
    mapUi.value       = H.ui.UI.createDefault(localMap, defaultLayers.value);
    map.value         = localMap;
    observeContainerSize();
  }
}

const showMarkerBubble = (event) => {
  const marker = event.target;
  const label  = marker.getData();
  if (!label || !mapUi.value) {
    return;
  }

  // The label comes from the HERE API, so it is fed to the bubble as a text node instead of
  // a string: `InfoBubble` renders a string as HTML.
  if (mapBubble.value) {
    mapBubble.value.setPosition(marker.getGeometry());
    mapBubble.value.setContent(document.createTextNode(label));
    mapBubble.value.open();
  } else {
    mapBubble.value = new H.ui.InfoBubble(marker.getGeometry(), {
      content: document.createTextNode(label)
    });
    mapUi.value.addBubble(mapBubble.value);
  }
};

const setMarker = (position, label) => {
  if (!map.value) {
    return;
  }

  if (mapMarker.value) {
    mapMarker.value.setGeometry(position);
  } else {
    mapMarker.value = new H.map.Marker(position);
    mapMarker.value.addEventListener("tap", showMarkerBubble);
    map.value.addObject(mapMarker.value);
  }
  mapMarker.value.setData(label ?? "");

  if (mapBubble.value) {
    mapBubble.value.setPosition(position);
    mapBubble.value.setContent(document.createTextNode(label ?? ""));
  }
};

const removeMarker = () => {
  if (mapBubble.value) {
    if (mapUi.value) {
      mapUi.value.removeBubble(mapBubble.value);
    }
    mapBubble.value = null;
  }

  if (mapMarker.value) {
    mapMarker.value.removeEventListener("tap", showMarkerBubble);
    if (map.value) {
      map.value.removeObject(mapMarker.value);
    }
    mapMarker.value = null;
  }
};

const geocodeAddress = async (query) => {
  let url = new URL(geocodeUrl);
  url.searchParams.append("q", query);
  url.searchParams.append("apiKey", apiKey);

  const response = await fetch(url.href);
  if (!response.ok) {
    throw Error(`Failed to fetch the geocoding information of the project address. Response status: ${response.status}`);
  }

  const data = await response.json();
  if (!Array.isArray(data.items) || data.items.length === 0) {
    return null;
  }

  const item     = data.items[0];
  const position = item.position ?? null;
  if (!position?.lat || !position?.lng) {
    return null;
  }

  return {
    lat  : position.lat,
    lng  : position.lng,
    zoom : ZOOM_BY_RESULT_TYPE[item.resultType] ?? DEFAULT_ZOOM,
    label: item.address?.label ?? item.title ?? query
  };
};

const showPosition = (result) => {
  mapCenter.value = {lat: result.lat, lng: result.lng};
  mapZoom.value   = result.zoom;

  if (map.value) {
    map.value.setCenter(mapCenter.value);
    map.value.setZoom(mapZoom.value);
  } else {
    initMap();
  }
  setMarker(mapCenter.value, result.label);
};

const updateMapLocation = async (info) => {
  if (!apiKey) {
    return;
  }

  const query = getAddressQuery(info);
  if (!query) {
    lastQuery     = null;
    removeMarker();
    status.value  = "empty";
    return;
  }

  // The watcher also fires for unrelated project info edits, so do not geocode again - and
  // do not reset the view the user may have panned - when the address itself did not change.
  if (query === lastQuery && status.value === "ready") {
    return;
  }

  status.value      = "loading";
  const currentCall = ++requestId;

  try {
    const result = await geocodeAddress(query);
    if (currentCall !== requestId) {
      return;
    }

    if (!result) {
      removeMarker();
      status.value = "notFound";
      return;
    }

    lastQuery    = query;
    showPosition(result);
    status.value = "ready";
  } catch (error) {
    console.error(error.message);
    if (currentCall === requestId) {
      removeMarker();
      status.value = "error";
    }
  }
};

// Watchers
watch(projectInfo, (newValue) => {
  updateMapLocation(newValue);
});

// Life cycle hooks
onMounted(() => {
  if (!apiKey) {
    return;
  }

  platform.value      = new H.service.Platform({apiKey: apiKey});
  defaultLayers.value = platform.value.createDefaultLayers();

  window.addEventListener("resize", resizeMap);

  updateMapLocation(projectInfo.value);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeMap);

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  if (map.value) {
    map.value.dispose();
    map.value = null;
  }

  mapMarker.value = null;
  mapBubble.value = null;
});
</script>

<template>
  <v-card class="card-full-height d-flex flex-column"
          elevation="3"
          :min-height="props.minHeight"
  >
    <v-card-text class="project-map-content">
      <template v-if="apiKey">
        <div ref="mapContainer"
             class="project-map-canvas"
        ></div>
        <div v-if="status !== 'ready'"
             class="project-map-overlay"
        >
          <v-progress-circular v-if="status === 'loading'"
                               color="secondary"
                               indeterminate
          />
          <div v-else
               class="text-body-1 text-center"
          >{{ $t(statusMessageKey) }}</div>
        </div>
      </template>
      <div v-else
           class="font-weight-bold text-h5"
      >{{ $t('components.projectMap.missingApiKey') }}</div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
@import '@/assets/styles/components/ProjectMap.css';
</style>
